import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { FALLBACK_CONCIERGES } from "../lib/fallbacks";
import TrustBadges from "../components/TrustBadges";
import { Star, MapPin, ArrowRight, Sparkles, Award, Shield, Gem, Clock, AlertTriangle, CalendarCheck2, Home as HomeIcon, Globe, Plane } from "lucide-react";

/**
 * ConciergePublic — public profile for a TREL Concierge.
 *
 * Route: /concierge/:slug          (e.g. /concierge/marcus-chen)
 *        /concierge/user_xxxxxxxx  (backend accepts user_id too)
 *
 * Customers click this page to evaluate the concierge as a person and choose
 * to start an experience with them. Intentionally NOT an experience-listing
 * page — there's no "Book This Activity" button. The single primary CTA opens
 * the AI Concierge with this concierge pre-attached to the request.
 *
 * Profile integrity rules:
 *   • Real concierges: full trust badges, portfolio, reviews, "Start Planning"
 *   • Demo / incomplete profiles: "Example Concierge Profile" banner, all
 *     badges hidden, booking disabled — no fake credibility.
 */
export default function ConciergePublic() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    api.get(`/concierges/${slug}`)
      .then((r) => setC(r.data))
      .catch(() => {
        // Fallback for static homepage concierges (Marcus/Sophia/David/Ava).
        // Important: fallback profiles are EXPLICITLY labeled as demo and
        // never show trust badges — preventing fake credibility on cold-start.
        const fallback = FALLBACK_CONCIERGES.find((f) => slugify(f.name) === slug);
        if (fallback) {
          setC({
            ...fallback,
            bio:
              "Example concierge profile. Once a real Concierge claims this profile and completes verification + TREL Certification, customers will see their full trust badges here.",
            slug,
            is_demo: true,
            profile_complete: false,
            concierge_status: "Example",
            // Force all trust flags off for demo profiles
            identity_verified: false,
            trel_certified: false,
            elite_badge_active: false,
            trel_plus_active: false,
            response_speed: "Demo profile",
            portfolio_images: [],
            example_experiences: [],
            reviews: [],
          });
        } else {
          setC(null);
          setErr("This concierge isn't available right now.");
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Fetch the concierge's availability separately — only for real (non-demo)
  // profiles since example concierges have no schedule. Refactored to an
  // async helper with a cancel-flag so setState happens inside an async
  // callback (sidesteps the react-hooks/set-state-in-effect false positive).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!c?.user_id || c?.is_demo) {
        if (!cancelled) setAvailability(null);
        return;
      }
      try {
        const r = await api.get(`/concierge/availability/${c.user_id}`);
        if (!cancelled) setAvailability(r.data);
      } catch (_) {
        if (!cancelled) setAvailability(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [c?.user_id, c?.is_demo]);

  if (loading) {
    return <div className="max-w-3xl mx-auto p-20 text-center text-[#071A2D]/60" data-testid="concierge-loading">Loading…</div>;
  }
  if (err || !c) {
    return (
      <div className="max-w-md mx-auto p-12 text-center" data-testid="concierge-not-found">
        <h1 className="font-serif text-3xl">{err || "Concierge not found"}</h1>
        <Link to="/explore" className="btn-coral inline-block mt-6">Browse concierges</Link>
      </div>
    );
  }

  const firstName = (c.name || "").split(" ")[0];
  // A profile is treated as a demo/example when ANY of these are true:
  //   - backend tagged the user/provider as is_demo or is_demo_profile
  //   - the public payload arrived through the fallback path with is_demo_profile
  //   - the profile is still incomplete (provider hasn't finished onboarding)
  const isDemo = c.is_demo || c.is_demo_profile || !c.profile_complete;
  const startPrompt = `I'd love to work with ${firstName} on a custom experience.`;
  const startCTA = () => nav(`/ai?q=${encodeURIComponent(startPrompt)}`);

  return (
    <div className="bg-[#FFF8EE]" data-testid={`concierge-page-${c.slug || slug}`}>
      {/* Example-profile banner — prominent, spec'd copy. Demo profiles cannot
          accept bookings/messages/payments and never display earned trust badges. */}
      {isDemo && (
        <div
          className="bg-[#071A2D] text-[#FFF8EE] py-3 px-6 text-center text-xs"
          data-testid="demo-profile-banner"
        >
          <AlertTriangle size={12} className="inline mr-1.5 text-[#C9A45C]"/>
          <strong>Example Concierge Profile</strong> · This is an example Concierge profile.{" "}
          <Link to="/signup" className="underline hover:text-[#C9A45C]">Create an account</Link>{" "}
          or <Link to="/explore" className="underline hover:text-[#C9A45C]">explore real Concierges</Link> when available.
        </div>
      )}

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-10">
        <Link to="/" className="text-xs uppercase tracking-[0.22em] text-[#064E3B] hover:underline">
          ← Back to TREL
        </Link>
        <div className="mt-6 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden shadow-luxury-lg bg-[#FFF8EE] aspect-[4/5]">
              {c.photo && (
                <img
                  src={c.photo}
                  alt={c.name}
                  className="w-full h-full object-cover"
                  data-testid="concierge-photo"
                />
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
              {isDemo ? "Example Profile" : "Trusted TREL Concierge"}
            </div>
            <h1
              className="font-serif text-5xl md:text-6xl mt-3 text-[#071A2D] leading-[1.05]"
              data-testid="concierge-name"
            >
              {c.name}
            </h1>
            {c.business_name && (
              <div className="mt-1 text-sm text-[#071A2D]/55 italic">{c.business_name}</div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#071A2D]/70">
              {(c.city || c.state) && (
                <span className="inline-flex items-center gap-1.5" data-testid="concierge-city">
                  <MapPin size={14}/> Based in {c.city}{c.state ? `, ${c.state}` : ""}
                </span>
              )}
              {!isDemo && (
                <>
                  <span className="inline-flex items-center gap-1.5" data-testid="concierge-rating">
                    <Star size={14} fill="#C9A45C" color="#C9A45C"/>
                    <span className="font-medium text-[#071A2D]">{Number(c.rating || 0).toFixed(1)}</span>
                  </span>
                  <span data-testid="concierge-moments">· {c.moments_created || 0} moments created</span>
                  {c.response_speed && (
                    <span className="inline-flex items-center gap-1.5 text-[#064E3B]" data-testid="concierge-response">
                      <Clock size={12}/> {c.response_speed}
                    </span>
                  )}
                  {availability?.next_available_date && (
                    <span
                      data-testid="concierge-next-available"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE] text-[11px] font-semibold"
                    >
                      <CalendarCheck2 size={12}/> Next available: {availability.next_available_date}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Service scope — quick glance pills showing how this concierge
                operates: local presence, remote planning, travel coordination.
                Per Phase 52 spec: every concierge profile must clarify these. */}
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="concierge-service-scope">
              {c.serves_locally !== false && (
                <span data-testid="scope-local" className="inline-flex items-center gap-1.5 rounded-full bg-[#064E3B]/8 text-[#064E3B] px-2.5 py-1 text-[12px] font-medium">
                  <HomeIcon size={12} strokeWidth={2}/> Local Support
                </span>
              )}
              {c.remote_available !== false && (
                <span data-testid="scope-remote" className="inline-flex items-center gap-1.5 rounded-full bg-[#00AEEF]/10 text-[#0277A5] px-2.5 py-1 text-[12px] font-medium">
                  <Globe size={12} strokeWidth={2}/> Remote Planning
                </span>
              )}
              {c.travel_coordination && (
                <span data-testid="scope-travel" className="inline-flex items-center gap-1.5 rounded-full bg-[#C9A45C]/15 text-[#8A6E2F] px-2.5 py-1 text-[12px] font-medium">
                  <Plane size={12} strokeWidth={2}/> Travel Coordination
                </span>
              )}
            </div>

            {/* Trust badges — only shown for approved, profile-complete concierges */}
            {!isDemo && (
              <div className="mt-5">
                <TrustBadges provider={c} size="md" />
              </div>
            )}

            {c.bio && (
              <p className="mt-7 text-[#071A2D]/80 leading-relaxed text-lg max-w-2xl" data-testid="concierge-bio">
                {c.bio}
              </p>
            )}

            {/* Specialties */}
            {Array.isArray(c.specializes) && c.specializes.length > 0 && (
              <div className="mt-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                  Specialties
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="concierge-specialties">
                  {c.specializes.map((s) => (
                    <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#064E3B]/8 text-[#064E3B]">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Starting price + primary CTA */}
            <div className="mt-9 rounded-3xl bg-[#071A2D] text-[#FFF8EE] p-7 shadow-luxury-lg flex flex-wrap items-center gap-6 justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
                  Custom experiences
                </div>
                <div className="font-serif text-3xl mt-1" data-testid="concierge-starting-price">
                  Services from <span className="text-[#C9A45C]">${c.starting_at || 49}</span>
                </div>
                <div className="text-xs opacity-70 mt-1">
                  Planning fee starts here. Final experience quote is shared after your request.
                </div>
              </div>
              {!isDemo ? (
                <button
                  onClick={startCTA}
                  data-testid="concierge-start-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF7A45] text-[#FFF8EE] px-6 py-3 font-semibold hover:brightness-110 transition"
                >
                  Start Planning With {firstName} <ArrowRight size={16}/>
                </button>
              ) : (
                <span className="rounded-full bg-[#FFF8EE]/15 px-5 py-3 text-xs font-semibold text-[#C9A45C]" data-testid="demo-disabled-cta">
                  Booking disabled · Example profile
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CUSTOMERS TRUST THIS CONCIERGE — explained badges */}
      {!isDemo && (c.identity_verified || c.trel_certified || c.elite_badge_active || c.trel_plus_active) && (
        <section className="max-w-6xl mx-auto px-6 py-12 border-t border-[#071A2D]/10" data-testid="trust-section">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold mb-2">
            Why customers trust this Concierge
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mt-1">Trust, earned step by step.</h2>
          <p className="mt-3 text-[#071A2D]/65 max-w-2xl">
            TREL stores no SSN, ID documents, or background-report content. We only display the trust statuses concierges have earned through our partners.
          </p>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.identity_verified && (
              <TrustExplainer
                color="#00AEEF"
                icon={Shield}
                label="✓ Identity Verified"
                meaning="Confirmed identity through secure verification."
              />
            )}
            {(c.trel_certified) && (
              <TrustExplainer
                color="#064E3B"
                icon={Award}
                label="⭐ TREL Certified"
                meaning="Completed TREL customer service and experience standards."
              />
            )}
            {c.elite_badge_active && (
              <TrustExplainer
                color="#C9A45C"
                icon={Gem}
                label="💎 TREL Elite Concierge"
                meaning="Completed TREL's highest trust requirements including enhanced screening."
              />
            )}
            {c.trel_plus_active && (
              <TrustExplainer
                color="#C9A45C"
                icon={Sparkles}
                label="✨ TREL+"
                meaning="Uses premium tools to create and manage experiences. (Growth subscription — not a trust signal.)"
                italic
              />
            )}
          </div>
        </section>
      )}

      {/* PORTFOLIO */}
      {!isDemo && Array.isArray(c.portfolio_images) && c.portfolio_images.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-12 border-t border-[#071A2D]/10" data-testid="concierge-portfolio">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold mb-2">Portfolio</div>
          <h2 className="font-serif text-3xl md:text-4xl">A taste of {firstName}&apos;s work.</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {c.portfolio_images.slice(0, 8).map((src, i) => (
              <div key={src + i} className="aspect-square rounded-2xl overflow-hidden shadow-luxury">
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"/>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* EXAMPLE EXPERIENCES — concierge's own past listings (inspiration, not products) */}
      {!isDemo && Array.isArray(c.example_experiences) && c.example_experiences.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-12 border-t border-[#071A2D]/10" data-testid="concierge-examples">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold mb-2">Example experiences</div>
          <h2 className="font-serif text-3xl md:text-4xl">Moments {firstName} has created.</h2>
          <p className="mt-3 text-[#071A2D]/65 max-w-2xl">
            Inspiration — not menu items. Your experience is custom-designed.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.example_experiences.slice(0, 6).map((ex) => (
              <div key={ex.experience_id} className="rounded-3xl card-luxury overflow-hidden">
                {ex.image && (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={ex.image} alt={ex.title} className="w-full h-full object-cover"/>
                  </div>
                )}
                <div className="p-4">
                  <div className="font-medium">{ex.title}</div>
                  <div className="text-xs text-[#071A2D]/60 mt-1">{ex.city} · {ex.duration}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* REVIEWS */}
      {!isDemo && Array.isArray(c.reviews) && c.reviews.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-12 border-t border-[#071A2D]/10" data-testid="concierge-reviews">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold mb-2">Customer reviews</div>
          <h2 className="font-serif text-3xl md:text-4xl">What guests are saying.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {c.reviews.slice(0, 4).map((r) => (
              <div key={`${r.created_at || ""}-${(r.by_first_name || "g").slice(0,8)}-${(r.text || "").slice(0, 24)}`} className="rounded-3xl card-luxury p-6">
                <div className="flex items-center gap-1">
                  {Array.from({ length: r.rating || 5 }).map((_, idx) => (
                    <Star key={`star-${idx}`} size={14} fill="#C9A45C" color="#C9A45C"/>
                  ))}
                </div>
                <p className="mt-3 text-[#071A2D]/85 italic leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-3 text-xs text-[#071A2D]/55">— {r.by_first_name}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How it works — three steps */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-[#071A2D]/10">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            {
              icon: Sparkles,
              title: "Share your vision",
              body: `Tell ${firstName} what you want — the moment, the people, the vibe. No briefs, no forms.`,
            },
            {
              icon: Award,
              title: "Bespoke plan",
              body: `${firstName} designs a complete experience: venues, timing, surprises, and the small details.`,
            },
            {
              icon: Star,
              title: "Live the moment",
              body: "Show up. Everything is handled. Pay securely on TREL — tips go 100% to your concierge.",
            },
          ].map((s) => (
            <div key={s.title} className="rounded-3xl card-luxury p-6">
              <s.icon className="text-[#FF7A45]" size={20} />
              <div className="font-serif text-2xl mt-3">{s.title}</div>
              <p className="text-sm text-[#071A2D]/70 mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          {!isDemo ? (
            <button
              onClick={startCTA}
              data-testid="concierge-start-cta-bottom"
              className="btn-coral inline-flex items-center gap-2"
            >
              Start Planning With This Concierge <ArrowRight size={16} />
            </button>
          ) : (
            <Link
              to="/become-provider"
              data-testid="demo-claim-cta"
              className="btn-coral inline-flex items-center gap-2"
            >
              Are you {firstName}? Claim this profile <ArrowRight size={16} />
            </Link>
          )}
          <div className="text-xs text-[#071A2D]/55 mt-3">
            One request. One concierge. One complete experience.
          </div>
        </div>
      </section>
    </div>
  );
}

const TrustExplainer = ({ color, icon: Icon, label, meaning, italic }) => (
  <div
    className="rounded-3xl p-5 bg-[#FFF8EE] border shadow-luxury"
    style={{ borderColor: `${color}33` }}
    data-testid={`trust-explainer-${label.replace(/[^a-z]/gi, "").toLowerCase()}`}
  >
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
      style={{ background: `${color}1A`, color }}
    >
      <Icon size={18}/>
    </div>
    <div className="font-medium text-[#071A2D]">{label}</div>
    <p className={`text-xs mt-1 leading-relaxed ${italic ? "italic text-[#071A2D]/60" : "text-[#071A2D]/70"}`}>
      {meaning}
    </p>
  </div>
);

// Local slugifier — must match the backend `_slugify_name`. Used for the
// FALLBACK_CONCIERGES lookup when the API returns 404 (e.g., before seed).
function slugify(name) {
  return (name || "")
    .toLowerCase()
    .split(/\s+/)
    .map((p) => p.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean)
    .join("-") || "concierge";
}
